import { css, html, LitElement } from 'lit';
import { FetchMixin } from '../FetchMixin';
import './DashboardNotificationList.js';
import './DashboardNotificationDetail.js';
import './DashboardAccountSetting.js';
import alertStore from '../AlertStore.js';
import { setAlert } from '../AlertSlice.js';

/**
 * ダッシュボードメニュー
 */
class DashboardMenu extends FetchMixin(LitElement) {
  static styles = css`
        :host {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .menu-button {
          background: transparent;
          cursor: pointer;
          padding: 0;
          border-radius: 50%;
          border-width: 1px;
          border-color: #4b5563; /* 境界線を少し明るく */
          border-style: solid;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          transition: all 0.2s ease;
        }

        .menu-button i {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: #d1d5db; /* grayから明るいグレー（シルバー）に変更 */
          transition: color 0.2s ease;
        }

        /* ホバー時の視認性強調 */
        .menu-button:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: #9ca3af;
        }

        .menu-button:hover i {
          color: #ffffff; /* ホバーで白く光らせる */
        }

        /* リンクの下線を消す */
        a {
          text-decoration: none;
        }

        .dashboard-menu-list {
          display: none;
          position: fixed;
          flex-direction: column;
          z-index: 1001;
          width: 250px;
          right: 20px;
          top: 60px;
          background: rgba(31, 41, 55, 0.98);
          border: 1px solid #4b5563;
          border-radius: 8px;
          box-shadow: rgba(0, 0, 0, 0.4) 0px 4px 15px;
        }

        /* PCでの視認性を考慮したモバイル対応：はみ出し防止 */
        @media (max-width: 640px) {
          .desktop-only-button {
            display: none;
          }
          .dashboard-menu-list {
            right: 10px;
            left: 10px;
            width: auto;
            top: 70px;
          }
          .menu-button {
            width: 38px;
            height: 38px;
          }
          .menu-button i {
            font-size: 18px;
          }
        }

        /* モバイルメニュー内でのみ表示する項目 */
        .mobile-only-item {
          display: none;
        }

        @media (max-width: 640px) {
          .mobile-only-item {
            display: block;
          }
        }

        .dashboard-menu-list-panel {
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }

        .dashboard-menu-item {
          flex-grow: 1;
          color: white;

          padding: 12px 20px;
          color: #e5e7eb;
          font-size: 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          border-bottom: 1px solid #374151;
        }

        .dashboard-menu-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }

        .notification-button {
          position: relative;
        }

        .notification-point {
          display: none;
          position: absolute;
          top: -2px;
          right: -2px;
        }

        .notification-point i {
          color: #ef4444;
          font-size: 10px;
        }

        /* identKey表示エリアのスタイル */
        .user-info {
          padding: 12px 20px;
          color: white;
          border-bottom: 1px solid #374151;
          display: flex;
          flex-direction: column;
        }

        .user-info-label {
          font-size: 10px;
          color: #9ca3af;
          margin-bottom: 4px;
        }

        .user-info-value {
          font-size: 14px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          word-break: break-all;
          color: #ffffff;
        }
        
        .user-info-value:hover {
            opacity: 0.8;
        }
        
        .user-info-value i {
            margin-left: 10px;
            font-size: 12px;
        }
    `;

  static properties = {
    // 新着のお知らせがあるか？
    hasNewNotifications: { type: Boolean },
    // ユーザー識別キー
    identKey: { type: String },
  };

  /**
   * コンストラクタ
   */
  constructor() {
    super();

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleChangeIdentKey = this.handleChangeIdentKey.bind(this);
    // this.boundHandleNotificationRead = this.handleNotificationRead.bind(this);

    this.hasNewNotifications = false;
    this.identKey = '';
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this.boundHandleClickOutside);
    // window.addEventListener('notification-read', this.boundHandleNotificationRead);
    // identKey変更イベントをリッスン
    window.addEventListener('changeIdentKey', this.boundHandleChangeIdentKey);

    this.addEventListener('unread-notification-status', this.handleUnreadNotificationStatus.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.boundHandleClickOutside);
    // window.removeEventListener('notification-read', this.boundHandleNotificationRead);
    window.removeEventListener('changeIdentKey', this.boundHandleChangeIdentKey);
    super.disconnectedCallback();
  }

  firstUpdated() {
    const fontAwasomeStyle = document.querySelector(
      "link[rel='stylesheet'][data-name='fontawesomev6']"
    );
    const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
    fontAwasomeStyleClone.removeAttribute('disabled');
    this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);
  }

  /**
   * identKey変更イベントハンドラ
   */
  handleChangeIdentKey(event) {
    if (event.detail && event.detail.identKey) {
      this.identKey = event.detail.identKey;
    }
  }

  /**
   * identKeyをクリップボードにコピー
   */
  copyIdentKey(event) {
    event.stopPropagation();
    if (this.identKey) {
      navigator.clipboard.writeText(this.identKey).then(() => {
        alertStore.dispatch(setAlert('Ident Keyをコピーしました'));
      }).catch(err => {
        console.error('コピーに失敗しました', err);
      });
    }
  }

  /**
   * メニューを開く
   */
  showAppMenu(event) {
    this.handleClickOutside(event);

    //id=appMenuListを表示する
    const appMenuList = this.shadowRoot.querySelector('#appMenuList');
    appMenuList.style.display = 'flex';
    event.stopPropagation();
  }

  /**
   * お知らせを開く
   * @param {*} event 
   */
  showNotificationMenu(event) {
    this.handleClickOutside(event);

    // id=appMenuListを表示する
    const notificationMenuList = this.shadowRoot.querySelector('#notificationMenuList');
    notificationMenuList.style.display = 'flex'; // flex に変更
    event.stopPropagation();
  }

  /**
   * 外部がクリックされたときの処理
   * @param {*} event 
   */
  handleClickOutside(event) {
    this.handleCloseMenu(event);
    this.handleCloseNotificationMenu(event);
    //this.handleCloseNotificationMenu(event);
  }

  /**
   * 未読のお知らせがあることのイベント通知のハンドル
   * @param {*} event 
   */
  handleUnreadNotificationStatus(event) {
    this.hasNewNotifications = event.detail.hasUnread;
  }

  /**
   * メニューを閉じる
   * @param {*} event 
   */
  handleCloseMenu(event) {
    const appMenuList = this.shadowRoot.querySelector('#appMenuList');
    if (appMenuList) appMenuList.style.display = 'none';
  }

  /**
   * お知らせを閉じる
   * @param {*} event 
   */
  handleCloseNotificationMenu(event) {
    const menuList = this.shadowRoot.querySelector('#notificationMenuList');
    if (menuList) menuList.style.display = 'none';
  }

  /**
   * ログアウト
   * @param {*} event 
   */
  async handleLogout(event) {
    try {
      // fetchオプションを設定
      const options = {
        method: 'DELETE',
      }

      // .envからエンドポイントを読み込み
      const endpoint = process.env.LOGOUT_ENDPOINT;
      let response = await this.fetchWithAuth(endpoint, options);
      if (response && response.status === 'logout_success') {
        window.location.reload();
      } else {
        throw new Error('ログアウトの処理に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching logout: ', error);
    }
  }

  /**
   * 全デバイスからログアウト
   * @param {*} event 
   */
  async handleLogoutAll(event) {
    try {
      // fetchオプションを設定
      const options = {
        method: 'DELETE',
      }

      // .envからエンドポイントを読み込み
      const endpoint = process.env.LOGOUT_ALL_ENDPOINT;
      // アプリケーション一覧を取得するRESTAPIを呼び出し
      let response = await this.fetchWithAuth(endpoint, options);
      if (response && response.status === 'all_device_logout_success') {
        window.location.reload();
      } else {
        throw new Error('ログアウトの処理に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching logout: ', error);
    }
  }

  /**
  * 設定を開く
  * @param {*} event 
  */
  handleOpenSetting(event) {
    this.handleClickOutside(event);

    // 既存の <dashboard-account-setting> があれば削除して重複を防ぐ
    const existingSetting = this.shadowRoot.querySelector('dashboard-account-setting');
    if (existingSetting) {
      existingSetting.remove();
    }

    // 設定画面コンポーネントを作成
    const accountSetting = document.createElement('dashboard-account-setting');
    this.shadowRoot.appendChild(accountSetting);
    accountSetting.show();
  }

  render() {
    // notification-pointのスタイルを動的に設定
    const notificationPointStyle = this.hasNewNotifications
      ? 'display: block;'
      : 'display: none;';

    return html`
        <!-- お知らせ -->
        <button class="menu-button notification-button" @click="${this.showNotificationMenu}" title="お知らせ">
          <i class="fa-solid fa-bell"></i>
          <div class="notification-point" style="${notificationPointStyle}"><i class="fa-solid fa-circle"></i></div>
        </button>
        
        <dashboard-notification-list id="notificationMenuList"></dashboard-notification-list>
        <dashboard-notification-detail></dashboard-notification-detail>
        
        <!-- ディスコード -->
        <a href="${process.env.DISCORD_ADDR}" target="_blank" rel="noopener noreferrer" class="desktop-only-button">
          <button class="menu-button notification-button" title="ディスコード"><i class="fa-brands fa-discord"></i></button>
        </a>

        <!-- マニュアル -->
        <a href="${process.env.BRAND_URL_BASE + 'manual'}" target="_blank" rel="noopener noreferrer" class="desktop-only-button">
          <button class="menu-button manual-button" title="マニュアル"><i class="fa-solid fa-book"></i></button>
        </a>

        <!-- ログアウト -->
        <button class="menu-button" @click="${this.showAppMenu}" title="メニュー"><i class="fa-solid fa-bars"></i></button>

        <div class="dashboard-menu-list" id="appMenuList">
          <div class="dashboard-menu-list-panel">
            <!-- モバイル時のみメニュー内に表示 -->
            <div class="dashboard-menu-item mobile-only-item" @click="${() => window.open(process.env.DISCORD_ADDR)}">ディスコード</div>
            <div class="dashboard-menu-item mobile-only-item" @click="${() => window.open(process.env.BRAND_URL_BASE + 'manual')}">マニュアル</div>

            <div class="dashboard-menu-item" @click="${this.handleOpenSetting}">設定</div>
            <div class="dashboard-menu-item" @click="${this.handleLogout}">ログアウト</div>
            <div class="dashboard-menu-item" @click="${this.handleLogoutAll}">全デバイスからログアウト</div>
            ${this.identKey ? html`
              <div class="user-info">
                <div class="user-info-label">Ident Key</div>
                <div class="user-info-value" @click="${this.copyIdentKey}" title="クリックしてコピー">
                    ${this.identKey}
                    <i class="fa-regular fa-copy"></i>
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      `;
  }
}

customElements.define('dashboard-menu', DashboardMenu);